import Analytics, { UserProperties } from 'analytics/Analytics'
import dayjs from 'dayjs'
import { createContext, ReactNode, useContext, useEffect } from 'react'
import { getChanges } from 'services/api/changes'
import {
  updateParticipantStatus,
  UpdateParticipantStatusBody
} from 'services/api/event'
import { getProfilesForParticipants, joinGroup } from 'services/api/group'
import ChangesService, { populateUnreadIfNeeded } from 'services/db/Changes'
import {
  Account,
  updatePushTokenIfNeeded
} from 'services/db/Queries/AccountQueries'
import {
  allGroups,
  ClearThirdPartyData,
  localGroupById
} from 'services/db/Queries/GroupQueries'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import {
  AccountDoc,
  ChangeDoc,
  ChangeDocsByTypes,
  DocType,
  EventDoc,
  EventParticipantStatus,
  GroupDoc,
  GroupStatus,
  ParticipantDeliveryStatusType
} from 'types/api/changes'
import { LocalStorageKey } from 'types/services/localStorage'
import { parseDocsBeforeSaving } from 'utils/api/changes'
import {
  isEventDetailsVisible,
  isGroupBlockedToAddEvents,
  isUserActiveInGroup
} from 'utils/api/groups'
import { isEmpty, isTrulyEmpty } from 'utils/object'

import { performUpdateGroupSettings } from 'components/popups/groupInfo/SettingsForCalendarSection'
import { isGroupArchived, isGroupHidden } from 'utils/api/userSettings'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { updateAccountOnServer } from 'services/api/account'
import Database from 'services/db/Database'
import { isConvertedGroupId } from 'utils/groups'
import * as Sentry from '@sentry/browser'
import { fetchAllFromThirdPartiesForSync } from 'utils/api/sync_local_calendars_to_server/sync_local_calendars_to_server'
import { isCypress } from 'utils/cypress'
import { OutlookContext } from './outlook/OutlookProvider'

interface GetChangesContextProps {}

interface GetChangesProviderProps {
  children: ReactNode
}

const GetChangesContext = createContext<GetChangesContextProps>({})

export default function GetChangesProvider(props: GetChangesProviderProps) {
  useEffect(() => {
    doGetChanges(
      LocalStorage.get(LocalStorageKey.DEVICE_ID),
      undefined,
      LocalStorage.get(LocalStorageKey.LAST_UPDATE)
    )
  }, [])

  return (
    <GetChangesContext.Provider value={{}}>
      {props.children}
    </GetChangesContext.Provider>
  )
}

export async function doGetChanges(
  deviceID?: string | null,
  groupIds?: string[] | undefined,
  lastUpdate?: string | null,
  ignoreEventsForMessage?: boolean,
  outlookContext?: OutlookContext
) {
  Logger.blue(
    'getting changes for groups and last update',
    groupIds,
    lastUpdate,
    ignoreEventsForMessage,
    outlookContext
  )

  try {
    const localGroups = await allGroups()
    Logger.pink('GETCHANGES:BEFORE', localGroups)

    const data = await getChanges({
      LastUpdate: lastUpdate,
      DeviceChangeID: deviceID,
      ForGroupsID: groupIds
    })
    const docs = parseDocsBeforeSaving(data.results)

    let groupsToFetch: string[] | undefined = undefined

    //to be sure that it's done *not* on 1st getchanges, we check it first
    if (lastUpdate && lastUpdate != null) {
      groupsToFetch = await gatherGroupsToUpdate(docs.Group)
    }

    if (groupsToFetch === undefined) {
      /**
       * Very first get changes
       */
      try {
        if (docs.Group)
          markUserAsInvitedIfNeeded(Object.values(docs.Group) as GroupDoc[])
      } catch (e) {
        Logger.debug('docs', docs)
        Logger.red('error get changes', e)
      }
    }

    if (data.now) {
      LocalStorage.set(LocalStorageKey.LAST_UPDATE, data.now)
    }

    if (docs[DocType.ACCOUNT]) {
      const account = Object.values(docs[DocType.ACCOUNT])[0] as AccountDoc
      if (account) {
        const UserID = account._id
        LocalStorage.set(LocalStorageKey.USER_ID, UserID)
        Analytics.getInstance().setAnalyticsUserId(UserID ?? 'none')
        Analytics.getInstance().appendSubscriptionInfo(account)
      }
    }

    Analytics.getInstance().updateLang()

    Logger.apiResponse('/general/changes', data, docs)
    if (isEmpty(docs)) {
      performActionsAfterGetChanges()
      return
    }

    const groups = docs.Group
    Logger.orange('processDownloadedData', docs)
    let skipUploadToServer = false
    /**
     * Before saving converted personal calendar, we need to remove old ones from DB
     * to prevent duplicates
     */
    if (groups) {
      for (const groupId in groups) {
        const group = groups[groupId] as GroupDoc

        if (
          group.CalendarEmail &&
          group.CalendarType &&
          group.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID)
        ) {
          const localPersonalGroup = (await allGroups()).find((local) => {
            return local._id === group.CalendarEmail
          })
          Logger.orange(
            'processDownloadedData',
            'Checking for personal group',
            localPersonalGroup
          )

          if (localPersonalGroup) {
            await ClearThirdPartyData(localPersonalGroup._id)
            skipUploadToServer = true
          }
        }
      }
    }

    // loop over all events and delete needSync field
    if (docs.GroupEvent) {
      for (const eventId in docs.GroupEvent) {
        const event = docs.GroupEvent[eventId] as EventDoc
        if (event.needSync) {
          delete event.needSync
        }
      }
    }

    await processDownloadedData(docs, ignoreEventsForMessage)

    if (
      (ignoreEventsForMessage === false ||
        ignoreEventsForMessage === undefined) &&
      lastUpdate != null
    ) {
      populateUnreadIfNeeded(docs, await UserSettings())
    }

    Logger.pink('GetChanges', 'Unread populated')

    Logger.pink('GetChanges:Join Group', 'Starting join group if needed')
    // this is the first get changes, we need to join group if needed
    await joinGroupIfNeeded()
    Logger.pink('GetChanges:Join Group', 'Done')

    if (!isCypress()) {
      try {
        Logger.pink('GetChanges', 'Updating push token if needed')
        if (
          (lastUpdate !== null && lastUpdate !== undefined) ||
          (await Notification.requestPermission()) === 'granted'
        ) {
          Logger.pink('GetChanges', 'Will update push token; Got permission')
          updatePushTokenIfNeeded()
        }
      } catch (e) {
        console.error('Error updating push token', e)
      }
      Logger.pink('GetChanges', 'Push token updated')
    }

    //performing it after all sync done, to prevent extra calls and endless loops
    if (groupsToFetch != undefined && groupsToFetch.length > 0) {
      await doGetChanges(null, groupsToFetch, null, true, outlookContext)

      Logger.orange(
        'GetChanges',
        'Done',
        docs,
        'will skip upload?',
        skipUploadToServer
      )

      Logger.pink('GetChanges', 'Groups fetched', groupsToFetch)
    }

    Logger.pink('GetChanges', 'Done')

    if (!groupIds || skipUploadToServer) {
      fetchAllFromThirdPartiesForSync(outlookContext)
    }

    return 1
  } catch (e) {
    Logger.red('GetChanges', e)
  }
}

export async function processDownloadedData(
  docs: ChangeDocsByTypes,
  ignoreEventsForMessage?: boolean
) {
  Logger.pink('GetChanges', 'Processing downloaded data', docs)
  return ChangesService.updateDocsMessage(docs, ignoreEventsForMessage ?? false)
    .then((docs) => {
      return ChangesService.saveChanges(docs)
    })
    .then(() => {
      loadParticipantsData(docs)
      if (isGroupcalApp()) sendDeliveredStatuses(docs)
      performActionsAfterGetChanges()
    })
}

async function sendDeliveredStatuses(docs: ChangeDocsByTypes) {
  if (!docs.GroupEvent) return
  const events = Object.values(docs.GroupEvent)
  if (events.length === 0) return
  const participantStatuses: UpdateParticipantStatusBody[] = []
  const today = dayjs()

  events
    .filter((doc) => {
      const event = doc as EventDoc
      const dayjsTime = dayjs(Number(event.StartDate) * 1000)
      const legit =
        dayjsTime.isSame(today, 'day') ||
        (dayjsTime.isAfter(today) && !isConvertedGroupId(event.GroupID))

      return legit
    })
    .forEach((doc) => {
      const event = doc as EventDoc
      const status: EventParticipantStatus = event.ParticipantsStatus?.[
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ] ?? {
        ParticipantDeliveryStatus: ParticipantDeliveryStatusType.DELIVERED,
        ParticipantConfirmationStatus: '1',
        LastUpdate: dayjs().unix().toString()
      }
      let numberedDeliveryStatus = 0

      if (status?.ParticipantDeliveryStatus)
        numberedDeliveryStatus = Number(status.ParticipantDeliveryStatus)

      if (
        status === undefined ||
        status.ParticipantDeliveryStatus === undefined ||
        numberedDeliveryStatus < Number(ParticipantDeliveryStatusType.DELIVERED)
      ) {
        const newStatus: UpdateParticipantStatusBody = {
          _id: event._id ?? '',
          ParticipantsStatus: {
            ...status,
            ParticipantDeliveryStatus: ParticipantDeliveryStatusType.DELIVERED
          },
          DeviceChangeID: LocalStorage.get(LocalStorageKey.DEVICE_ID) ?? ''
        }
        participantStatuses.push(newStatus)
      }
    })

  if (participantStatuses.length == 0) return

  await updateParticipantStatus(participantStatuses)
}

function loadParticipantsData(docs: ChangeDocsByTypes) {
  /**
   * for all received groups, we update participants table
   */
  let participantIds: string[] = []
  if (docs.Group) {
    Object.keys(docs.Group).forEach((groupId) => {
      const group = docs.Group[groupId] as GroupDoc

      if (group.Participants) {
        const participantsOfGroup = Object.keys(group.Participants)
        participantIds = [...participantIds, ...participantsOfGroup]
      }
    })

    getProfilesForParticipants(
      participantIds.filter(function (elem, index, self) {
        return index === self.indexOf(elem)
      })
    )
  }
}

export function useGetChanges() {
  return useContext(GetChangesContext)
}
async function gatherGroupsToUpdate(
  groupsFromServer: Record<string, ChangeDoc>
) {
  if (!groupsFromServer) return []
  const groups = Object.values(groupsFromServer)
  Logger.pink('GETCHANGES:', groups)
  const localGroups = await allGroups()
  Logger.pink('GETCHANGES:', localGroups)
  let groupsToFetch: string[] | undefined = []
  if (groups) {
    groups.forEach((groupFromServer) => {
      const existingGroup = localGroups.find(
        (group) => group._id === groupFromServer._id
      ) as GroupDoc
      const serverGroup = groupFromServer as GroupDoc
      const userId = LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      if (existingGroup) {
        if (
          isUserActiveInGroup(serverGroup, userId) &&
          !isUserActiveInGroup(existingGroup, userId)
        ) {
          Logger.pink('GETCHANGES:', 'user was added to group')
          groupsToFetch?.push(groupFromServer._id ?? '')
        }

        // check if group has a setting to hide event details other than the one in the server
        Logger.pink(
          'GETCHANGES:',
          existingGroup.Name,
          existingGroup.GroupSettings.IsAllParticipantsCanSeeFreeBusyOnly,
          serverGroup.GroupSettings.IsAllParticipantsCanSeeFreeBusyOnly,
          isEventDetailsVisible(existingGroup),
          isEventDetailsVisible(serverGroup)
        )
        if (
          (isEventDetailsVisible(existingGroup) &&
            !isEventDetailsVisible(serverGroup)) ||
          (!isEventDetailsVisible(existingGroup) &&
            isEventDetailsVisible(serverGroup))
        ) {
          groupsToFetch?.push(groupFromServer._id ?? '')
        }
      } else {
        groupsToFetch?.push(groupFromServer._id ?? '')
      }
    })
  }

  if (groupsToFetch?.length === 0) groupsToFetch = []

  return groupsToFetch
}

function markUserAsInvitedIfNeeded(groups: GroupDoc[]) {
  const hasNonGenericGroups = groups.some((group) => {
    return !group.DeviceChangeID?.includes('Prepopulated_Group')
  })

  if (
    (hasNonGenericGroups ||
      LocalStorage.get(LocalStorageKey.CACHED_GROUP_ID_JOIN)) &&
    LocalStorage.get(LocalStorageKey.NEW_USER)
  ) {
    Logger.debug('sending invited status')
    Analytics.getInstance().setUserProperties({
      key: UserProperties.INVITED,
      value: 'Yes'
    })
  }
}

export async function joinGroupIfNeeded() {
  const groupId = LocalStorage.get(LocalStorageKey.CACHED_GROUP_ID_JOIN)
  const groupPassword = LocalStorage.get(LocalStorageKey.CACHED_GROUP_PASSWORD)

  if (isTrulyEmpty(groupId) || isTrulyEmpty(groupPassword)) {
    Logger.blue('Join group:No groupId found in localStorage')
    return undefined
  }

  Logger.blue('Join group', groupId, groupPassword)
  const group = await localGroupById(groupId ?? '')
  Logger.blue('Join group:group', group)
  const isActive =
    group &&
    isUserActiveInGroup(group, LocalStorage.get(LocalStorageKey.USER_ID) ?? '')

  if (groupId) {
    const userSettings = await UserSettings()
    if (group && isGroupArchived(userSettings, groupId)) {
      // unarchive group first
      await performUpdateGroupSettings(userSettings, groupId, {
        GroupStatus: GroupStatus.ACTIVE
      })

      Logger.blue('Join group:Unarchived group', groupId)
    }
  }

  /**
   * Remove to prevent endless loop
   */
  LocalStorage.remove(LocalStorageKey.CACHED_GROUP_ID_JOIN)
  LocalStorage.remove(LocalStorageKey.CACHED_GROUP_PASSWORD)

  if (isActive) {
    Logger.blue('Join group:User is already active in group', groupId)

    return undefined
  }

  if (groupId && groupPassword && (!group || !isActive)) {
    return await joinGroup(groupId, groupPassword)
  }
}

function performActionsAfterGetChanges() {
  setDefaultCalendarIfNeeded()
  updatePhoneNumberTypeIfNeeded()

  Account().then((account) => {
    if (!account) return

    Sentry.setUser({
      id: LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
      email: is24meApp() ? account.Email : account.PhoneNumber
    })
  })
}

export async function getDefaultCalendar() {
  const userSettings = await UserSettings()

  const id =
    userSettings &&
    userSettings.GroupsSettings &&
    userSettings.GroupsSettings != null
      ? Object.entries(userSettings.GroupsSettings).find(
          ([key, value]) =>
            value.Default === '1' && !isGroupArchived(userSettings, key)
        )?.[0]
      : undefined

  return localGroupById(id ?? '')
}

export async function setDefaultCalendarIfNeeded(
  excludeGroup?: string | undefined
) {
  const userSettings = await UserSettings()
  const account = await Account()

  const id =
    userSettings &&
    userSettings.GroupsSettings &&
    userSettings.GroupsSettings != null
      ? Object.entries(userSettings.GroupsSettings).find(
          ([key, value]) =>
            value.Default === '1' && !isGroupArchived(userSettings, key)
        )?.[0]
      : undefined

  Logger.blue('default calendar', `current id: ${id}`)

  if (
    !id ||
    (id &&
      isGroupBlockedToAddEvents(
        account,
        await localGroupById(id),
        userSettings,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ))
  ) {
    allGroups().then((groups) => {
      if (!groups || groups.length === 0) return undefined
      const newDefaultGroupId =
        groups
          .filter((group) => {
            return !isGroupArchived(userSettings, group._id)
          })
          .find((group) => {
            return (
              !isGroupBlockedToAddEvents(
                account,
                group,
                userSettings,
                LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
              ) &&
              excludeGroup != group._id &&
              !isGroupHidden(userSettings, group._id)
            )
          })?._id ?? ''

      Logger.blue('default calendar', `new cal id: ${newDefaultGroupId}`)
      if (newDefaultGroupId && newDefaultGroupId.length > 0) {
        return performUpdateGroupSettings(userSettings, newDefaultGroupId, {
          Default: '1'
        })
      }
    })
  }
}

async function updatePhoneNumberTypeIfNeeded() {
  const account = await Account()
  if (!account) return
  const current = LocalStorage.get(LocalStorageKey.PHONE_NUMBER_TYPE)
  const fromAccount = account.PhoneNumberType

  if (current && current !== fromAccount) {
    await Database.write(async () => {
      account.update((acc) => {
        acc.PhoneNumberType = current
      })
    })

    await updateAccountOnServer({
      ...(Database.toServerObject(account) as AccountDoc),
      PhoneNumberType: current
    })
  }
}
